import { ApolloClient, gql, useApolloClient } from "@apollo/client";
import { sleep } from "@hex/common";
import { isEmpty } from "lodash";
import {
  CancellationToken,
  editor as Editor,
  Position,
  Range,
  languages,
} from "monaco-editor";
import React, { useContext, useMemo } from "react";

import { MagicCompletionData } from "../../../cell/monaco/KernelCompletionProvider";

import { PUBLIC_HEX_SL_FILE_YAML_LANGUAGE } from "./ids";
import {
  GetSemanticWorkbenchCompletionsDocument,
  GetSemanticWorkbenchCompletionsQuery,
  GetSemanticWorkbenchCompletionsQueryVariables,
} from "./SemanticWorkbenchTypeaheadCompletionProvider.generated";

gql`
  query GetSemanticWorkbenchCompletions(
    $editableRegion: String!
    $prefix: String!
    $suffix: String!
  ) {
    getSemanticWorkbenchCompletions(
      editableRegion: $editableRegion
      prefix: $prefix
      suffix: $suffix
    ) {
      result
    }
  }
`;

const INLINE_COMPLETION_DEBOUNCE = 150;
const DEBUG = false;

// Quick little log helper to make it easy to commit debug logs
// until this is working really well. Its useful to understand
// why we're not getting completions when that happens.
const debugLog = (...args: unknown[]): void => {
  if (DEBUG) {
    // eslint-disable-next-line no-console -- I want to see these logs
    console.log(...args);
  }
};

export class SemanticWorkbenchTypeaheadCompletion
  implements languages.InlineCompletionsProvider
{
  constructor(private client: ApolloClient<unknown>) {}
  public lastCompletion?: MagicCompletionData;
  private isSemanticWorkbenchTypeaheadEnabled?: boolean;

  public update({
    isSemanticWorkbenchTypeaheadEnabled,
  }: {
    isSemanticWorkbenchTypeaheadEnabled: boolean;
  }): void {
    this.isSemanticWorkbenchTypeaheadEnabled =
      isSemanticWorkbenchTypeaheadEnabled;
  }

  // eslint-disable-next-line max-params -- this is a monaco API method
  async provideInlineCompletions(
    model: Editor.ITextModel,
    position: Position,
    _context: languages.InlineCompletionContext,
    token: CancellationToken,
  ): Promise<languages.InlineCompletions | undefined> {
    await sleep(INLINE_COMPLETION_DEBOUNCE);
    if (token.isCancellationRequested) {
      debugLog("Returning undefined because it was requested", {
        tokenIsCancellationRequested: token.isCancellationRequested,
      });
      return undefined;
    }

    if (!this.isSemanticWorkbenchTypeaheadEnabled) {
      debugLog(
        "Returning undefined because semantic workbench typeahead is not enabled",
      );
      return undefined;
    }

    // Don't return anything if theres no text in the cell
    if (
      model.getValueLengthInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: model.getLineCount(),
        endColumn: model.getLineMaxColumn(model.getLineCount()),
      }) === 0
    ) {
      debugLog("Returning undefined because there's no text in the file");
      return undefined;
    }

    const { editableRegion, prefix, suffix } = this.getRegions(model, position);

    let result = undefined;
    try {
      debugLog("Getting completions", {
        prefix,
        editableRegion,
        suffix,
      });
      const { data } = await this.client.query<
        GetSemanticWorkbenchCompletionsQuery,
        GetSemanticWorkbenchCompletionsQueryVariables
      >({
        query: GetSemanticWorkbenchCompletionsDocument,
        variables: {
          prefix,
          editableRegion,
          suffix,
        },
      });
      result = data.getSemanticWorkbenchCompletions.result ?? "";
    } catch (_e) {
      debugLog("Returning undefined because of error", { error: _e });
      return undefined;
    }

    if (isEmpty(result)) {
      debugLog("Returning undefined because the result is empty");
      return undefined;
    } else {
      debugLog("Got a result from the API!", result);
    }

    // Get the text on the current line
    const currentLineText = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });

    // Check if the current line text is a prefix of the result
    if (!result.startsWith(currentLineText)) {
      debugLog(
        "Returning undefined because the result does not start with the current line text",
        {
          result,
          currentLineText,
        },
      );
      return undefined;
    }

    return {
      items: [
        {
          insertText: result,
          // Replace the current line with the new text
          range: new Range(
            position.lineNumber,
            1,
            position.lineNumber,
            model.getLineMaxColumn(position.lineNumber),
          ),
        },
      ],

      enableForwardStability: true,
    };
  }

  freeInlineCompletions(
    _completions: languages.InlineCompletions<languages.InlineCompletion>,
  ): void {
    return;
  }

  private getRegions(
    model: Editor.ITextModel,
    position: Position,
  ): {
    prefix: string;
    editableRegion: string;
    suffix: string;
  } {
    // Grab everything in the cell before the current line
    const prefix =
      position.lineNumber === 1
        ? ""
        : model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber - 1,
            endColumn: model.getLineMaxColumn(position.lineNumber - 1),
          });

    // Grab only the current line with the cursor
    const editableRegion = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: model.getLineMaxColumn(position.lineNumber),
    });

    // Grab everything from the next line to the end
    const suffix =
      position.lineNumber === model.getLineCount()
        ? ""
        : model.getValueInRange({
            startLineNumber: position.lineNumber + 1,
            startColumn: 1,
            endLineNumber: model.getLineCount(),
            endColumn: model.getLineMaxColumn(model.getLineCount()),
          });

    return {
      prefix,
      editableRegion,
      suffix,
    };
  }
}

const SemanticWorkbenchTypeaheadContext =
  React.createContext<SemanticWorkbenchTypeaheadCompletion | null>(null);

export const SemanticWorkbenchTypeaheadProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const client = useApolloClient();
  const semanticWorkbenchTypeaheadCompletion = useMemo(
    () => new SemanticWorkbenchTypeaheadCompletion(client),
    [client],
  );
  return (
    <SemanticWorkbenchTypeaheadContext.Provider
      value={semanticWorkbenchTypeaheadCompletion}
    >
      {children}
    </SemanticWorkbenchTypeaheadContext.Provider>
  );
};

export const useSemanticWorkbenchTypeaheadCompletion =
  (): SemanticWorkbenchTypeaheadCompletion => {
    const context = useContext(SemanticWorkbenchTypeaheadContext);
    if (!context) {
      throw new Error("SemanticWorkbenchTypeaheadCompletion not found");
    }
    return context;
  };

export const initSemanticWorkbenchTypeaheadCompletionProvider = (
  semanticWorkbenchTypeaheadCompletion: SemanticWorkbenchTypeaheadCompletion,
) => {
  languages.registerInlineCompletionsProvider(
    PUBLIC_HEX_SL_FILE_YAML_LANGUAGE.id,
    semanticWorkbenchTypeaheadCompletion,
  );
};
